/**
 * Module: Slide Page Types
 *
 * The layout vocabulary every slide template is built from. A template (a
 * `slide_templates` row) is an ordered list of pages; each page names the
 * layout types its slide may use, and the generation prompt injects the
 * matching `guidance` string so the model knows what that layout owes the
 * reader.
 *
 * Why this catalog is CODE and templates are DATA: the catalog is a contract
 * shared by the prompt builder, the seed validator, and the tests — it has to
 * version with the code that consumes it. Template rows are content ops can
 * tune without a deploy, and they only ever reference these slugs.
 *
 * Two axes are deliberately kept apart, following how the presentation
 * industry models this (PowerPoint layouts, Google Slides `PredefinedLayout`,
 * Beautiful.ai Smart Slides, Slidev layouts):
 *
 * - **Page type** (this file) — the layout + content contract. Reusable
 *   across every kind of deck.
 * - **Narrative role** (`SlideTemplatePage.role`) — what the page SAYS in one
 *   specific deck ("Traction", "The Ask"). Deck-specific.
 *
 * Collapsing the two is what forces a new type for every deck kind. Keeping
 * them apart is what lets ~18 types serve pitch, sales, review, and training
 * decks alike.
 *
 * Sizing note: 18 types is inside the industry band (Google Slides 11,
 * PowerPoint 9, Slidev 19, Beautiful.ai ~10 families). Variants — image left
 * vs right, one big number vs four — belong INSIDE a type's guidance, never
 * as new top-level types: past ~20 the model's type choice gets unreliable.
 */

/** One entry in the shared slide layout catalog. */
export interface SlidePageTypeDef {
  /** Stable slug referenced by `SlideTemplatePage.types`. */
  type: string;
  /**
   * `structural` pages carry the deck's skeleton and pacing (cover, divider,
   * closing); `content` pages carry the substance. Only used for validation
   * and authoring clarity — the prompt treats both the same.
   */
  kind: "structural" | "content";
  /**
   * Layout + content contract for this type, injected verbatim into the
   * generation prompt for the types a template actually uses. Written as
   * instructions to the generator, with hard caps stated inline: explicit
   * per-type limits are what keep a model from over-filling a layout.
   */
  guidance: string;
}

/** Type slug for the reserved cover page — exactly one, always first. */
export const SLIDE_PAGE_TYPE_START = "start";
/** Type slug for the reserved closing page — exactly one, always last. */
export const SLIDE_PAGE_TYPE_END = "end";

/**
 * The shared layout catalog.
 *
 * Every content type's guidance ends with the assertion-headline rule: a
 * heading should state the takeaway ("Retention doubled after onboarding
 * changed"), not the topic ("Retention"). This is the single most
 * evidence-backed slide-quality rule in the presentation literature
 * (assertion-evidence research, Alley et al.), and it costs one clause per
 * type to enforce.
 */
export const SLIDE_PAGE_TYPES: readonly SlidePageTypeDef[] = [
  // ---- Structural ---------------------------------------------------------
  {
    type: SLIDE_PAGE_TYPE_START,
    kind: "structural",
    guidance:
      'Cover slide. An `h1` hero title, one short line beneath it saying what this deck is, and presenter/organisation/date as small muted text. Full-bleed or strongly branded background, centered layout, no bullets and no body copy.',
  },
  {
    type: SLIDE_PAGE_TYPE_END,
    kind: "structural",
    guidance:
      "Closing slide. One clear call to action or ask, plus contact details if the brief gives any. Mirrors the cover visually. Never a bare \"Thank you\" — the last thing on screen should be what you want the audience to DO.",
  },
  {
    type: "agenda",
    kind: "structural",
    guidance:
      "Agenda slide. A numbered list of the deck's sections, 3-7 items, each a short phrase. Generous line spacing, no sub-bullets, no explanatory paragraphs.",
  },
  {
    type: "section",
    kind: "structural",
    guidance:
      "Section divider. An oversized section number and a short title on a high-contrast accent background, near-empty otherwise. Used to reset pacing between major parts of a longer deck; carries no content of its own.",
  },

  // ---- Content ------------------------------------------------------------
  {
    type: "bullets",
    kind: "content",
    guidance:
      "Bulleted points. A heading plus 3-5 short bullets, each under 12 words, optionally icon-led. The general-purpose fallback — reach for a more specific layout whenever the content fits one, because a deck of nothing but bullets reads as an outline chopped into pages. Heading states the takeaway, not the topic.",
  },
  {
    type: "statement",
    kind: "content",
    guidance:
      "Single big idea. One sentence set in oversized centered type, optionally with a small kicker line above or a short support line below. Nothing else on the slide — the whitespace is the point. Heading states the takeaway, not the topic.",
  },
  {
    type: "big-stat",
    kind: "content",
    guidance:
      "Metric slide. One to four figures, each as `{value, label, short context}`. A single figure becomes a hero number (text-7xl or larger) with its label beneath; two to four render as an evenly-weighted tile row. Only use numbers present in the brief — never invent a figure to fill a tile. The heading states what the number MEANS, not the metric name.",
  },
  {
    type: "quote",
    kind: "content",
    guidance:
      "Testimonial or quote. One quotation in large type with visible quote marks, attributed to a name, role, and organisation. Exactly one quote per slide, nothing else competing with it. Never fabricate a quote or an attribution. Any heading states the takeaway the quote proves, not the topic.",
  },
  {
    type: "chart",
    kind: "content",
    guidance:
      "Data slide. One CSS chart (see the chart recipes below) built from figures the brief actually provides, with the numbers also visible as text and a short source note. The heading states what the data PROVES, not what it shows. One chart per slide.",
  },
  {
    type: "table",
    kind: "content",
    guidance:
      "Structured comparison table. Keep it to roughly 5 columns by 6 rows, use zebra striping, and visually highlight the row or column the audience should land on. Good for pricing tiers, feature matrices, and reference data. The heading states the conclusion the table supports, not the topic.",
  },
  {
    type: "comparison",
    kind: "content",
    guidance:
      "Side-by-side comparison. Two or three labelled columns (before/after, us vs them, option A/B/C), each a titled card with 2-4 short points, laid out symmetrically. Give the preferred side more visual weight — colour or border — so the conclusion reads at a glance. The heading states which side wins and why, not the topic.",
  },
  {
    type: "quadrant",
    kind: "content",
    guidance:
      "Two-by-two matrix. Either labelled axes with items positioned in the four cells, or four titled cells (SWOT-style). Maximum 4 short items per cell. Use for positioning, prioritisation, or trade-off analysis. The heading states the position the matrix proves, not the topic.",
  },
  {
    type: "timeline",
    kind: "content",
    guidance:
      "Chronology or roadmap. Three to seven milestones as `{phase or date, title, one short line}` along a horizontal spine, switching to a vertical list above five. Mark the present moment when the brief implies one. The heading states the trajectory, not the topic.",
  },
  {
    type: "process",
    kind: "content",
    guidance:
      "Sequence of steps. Three to six numbered steps, each a title plus one short line, rendered as linear chevrons, a funnel, or a cycle depending on the shape of the content. Use for how-it-works, methodology, and workflows — not for chronology (use a timeline). The heading states the outcome the sequence produces, not the topic.",
  },
  {
    type: "team",
    kind: "content",
    guidance:
      "People slide. Two to eight cards, each with a photo placeholder, name, role, and at most one line of credential. Uniform card size and photo crop. Only include people the brief names. The heading states why this team wins, not the topic.",
  },
  {
    type: "image-full",
    kind: "content",
    guidance:
      "Full-bleed visual moment — a product hero shot, a diagram, or an emotional image, drawn as a labelled placeholder block since no external images can be fetched. At most one line of overlay text, and only over a contrast scrim so it stays readable. Any overlay line states the takeaway, not the topic.",
  },
  {
    type: "split",
    kind: "content",
    guidance:
      "Text beside a visual. A heading and 2-4 short points on one side, a single visual (placeholder, diagram, or small chart) on the other, at a 40/60 or 50/50 split; either side may lead. The workhorse for explaining a feature or a claim with its evidence next to it. Heading states the takeaway.",
  },
  {
    type: "cards",
    kind: "content",
    guidance:
      "Grid of parallel items — features, benefits, services, FAQ pairs, or a logo wall. Three to six uniform cards, each an icon or small heading plus one or two lines. Equal visual weight across cards; if one matters more, use a different layout. Heading states the takeaway, not the topic.",
  },
] as const;

/** Every valid page-type slug, for template validation and prompt lookups. */
export const SLIDE_PAGE_TYPE_SLUGS: readonly string[] = SLIDE_PAGE_TYPES.map(
  (definition) => definition.type,
);

/** Catalog indexed by slug for O(1) guidance lookups while building prompts. */
const BY_SLUG = new Map<string, SlidePageTypeDef>(
  SLIDE_PAGE_TYPES.map((definition) => [definition.type, definition]),
);

/**
 * Looks up one page type's definition.
 *
 * @param type - Page-type slug, e.g. `big-stat`.
 * @returns The definition, or `undefined` for an unknown slug. Callers treat
 * unknown slugs as "skip this type" rather than throwing — a stale template
 * row must never break generation (see the fallback matrix in the handler).
 */
export function getSlidePageType(type: string): SlidePageTypeDef | undefined {
  return BY_SLUG.get(type);
}

/** Whether a slug exists in the catalog. Used by seed-time validation. */
export function isValidSlidePageType(type: string): boolean {
  return BY_SLUG.has(type);
}
